import { JobTableConfig } from '../redux/application/application.types';
import { hasMultipleValues } from '../helper/GroupHelper';
import { FIELD_CATALOG, FIELD_CATALOG_BY_KEY, FIELD_CATALOG_KEYS } from './fieldCatalog';
import { warnOnce } from './warnOnce';

/**
 * Die aufgelöste Sichtbarkeit für eine Ansicht. Ein Objekt statt zweier
 * Config-Quellen: wer damit arbeitet, muss nicht wissen, ob eine Angabe
 * portalweit oder nur in dieser Tabelle weggenommen wurde.
 */
export interface FieldVisibility {
    /** Ist diese Angabe hier unsichtbar? */
    isHidden(key: string): boolean;
    /**
     * Gibt den Wert zurück, oder undefined, wenn die Config die Angabe
     * weggenommen hat. Gedacht für splitJoin, das undefined ohnehin wegfiltert:
     *
     *     splitJoin(', ', v.pick('customer.lastname', lastname), v.pick('customer.firstname', firstname))
     *
     * Entscheidet NICHT, ob ein Wert leer ist — „0" als Hausnummer ist eine
     * Angabe. Das Wegfiltern leerer Werte bleibt bei splitJoin.
     */
    pick<T>(key: string, value: T): T | undefined;
}

/** Für Aufrufer und Tests ohne jede Config. */
export const ALWAYS_VISIBLE: FieldVisibility = {
    isHidden: () => false,
    pick: (_key, value) => value,
};

/**
 * Entfaltet features.fieldVisibility.globalHidden zur Menge aller wirklich
 * versteckten Keys: eine Angabe ist versteckt, wenn sie selbst genannt ist ODER
 * irgendein Träger auf ihrem belongsTo-Pfad genannt ist.
 *
 * Die Kaskade gilt NUR hier, nicht für fieldVisibility.jobTables.*.columns. Die
 * portalweite Liste sagt „diese Angabe ist im Portal nicht zu sehen" — fachlich,
 * also kaskadiert es. Ein columns-Eintrag sagt „diese Spalte bzw. dieser
 * Bestandteil ist in dieser Tabelle nicht zu sehen" — Darstellung, also trifft er
 * genau den genannten Key. Kaskadierte er dort auch, nähme
 * { key: 'meter.meterAddress.full', visible: false } in jobSelection die
 * Einzelspalten Straße, PLZ und Ort mit, die heute stehen bleiben.
 *
 * Eine vertippte Config darf nichts leeren: unbekannte Keys werden ignoriert und
 * einmalig gemeldet. Weil die Config in Mongo Compass gepflegt wird und damit
 * jede serverseitige Prüfung umgeht, ist das die einzige Absicherung.
 */
export function resolveHiddenFields(globalHidden: string[] | undefined): Set<string> {
    const named = new Set<string>();

    (globalHidden ?? []).forEach((key) => {
        if (!FIELD_CATALOG_KEYS.has(key)) {
            warnOnce(
                `fieldVisibility:${key}`,
                `[fieldVisibility] Unbekannte Angabe "${key}" in features.fieldVisibility.globalHidden — Eintrag wird ignoriert. Erlaubte Keys: src/fieldVisibility/fieldCatalog.ts`,
            );
            return;
        }
        named.add(key);
    });

    if (named.size === 0) return new Set<string>();

    const resolved = new Set<string>();

    FIELD_CATALOG.forEach((entry) => {
        // Der seen-Satz ist Versicherung, nicht Notwendigkeit: der Vertragstest
        // hält fest, dass der Katalog keinen Zyklus enthält. Diese Funktion läuft
        // bei jedem Laden der Config — eine Endlosschleife hier legte das Portal
        // still, und die Versicherung kostet nichts.
        const seen = new Set<string>();
        let cursor: string | undefined = entry.key;

        while (cursor !== undefined && !seen.has(cursor)) {
            if (named.has(cursor)) {
                resolved.add(entry.key);
                return;
            }
            seen.add(cursor);
            cursor = FIELD_CATALOG_BY_KEY.get(cursor)?.belongsTo;
        }
    });

    return resolved;
}

/**
 * Vereinigt die portalweite Liste mit der Config einer Tabelle. Keine Quelle
 * holt zurück, was die andere weggenommen hat — deshalb braucht es keine
 * Rangfolge. Ohne Tabelle (Detailansichten, Listen) bleibt es bei der globalen
 * Liste.
 *
 * Die Menge entsteht einmal je Config, nicht einmal je Zelle: die Prädikate
 * laufen für jede Zeile jeder Tabelle, die Config ändert sich dabei nicht.
 */
export function fieldVisibilityFor(globalHidden: Set<string>, tableConfig?: JobTableConfig): FieldVisibility {
    const hidden = new Set(globalHidden);

    (tableConfig?.columns ?? []).forEach((entry) => {
        // visible: true wird ignoriert — die Config kann nur wegnehmen.
        if (entry.visible === false) hidden.add(entry.key);
    });

    return {
        isHidden: (key) => hidden.has(key),
        pick: (key, value) => (hidden.has(key) ? undefined : value),
    };
}

/**
 * Bleibt von diesen Bestandteilen mindestens einer übrig? Beantwortet der
 * Komponente, ob eine zusammengesetzte Zelle überhaupt noch etwas trägt. Eine
 * leere Liste heißt „nichts zu zeigen" — nicht „alles zeigen".
 */
export function hasVisibleField(visibility: FieldVisibility, keys: string[]): boolean {
    return keys.some((key) => !visibility.isHidden(key));
}

/**
 * Wie hasMultipleValues, aber nur über die Bestandteile, die die Config übrig
 * lässt: sonst bliebe eine Spalte stehen, in der bei jeder Zeile dasselbe steht
 * — genau das, was die Automatik verhindern soll.
 *
 * Setzt voraus, dass der Config-Key dem Feldpfad am Auftrag entspricht. Für
 * diese Bestandteile gilt das; der Katalog führt bewusst die echten Feldpfade
 * des Auftragsmodells.
 *
 * NUR für Bestandteile gedacht. Ein Spalten-Key gehört hier nicht hinein: das
 * Ausblenden von Spalten regelt die Spalten-Pipeline, und ein gemeinsamer
 * Automatik-Schalter mehrerer Spalten würde sonst umkippen, sobald eine
 * einzelne davon ausgeblendet wird.
 */
export function hasMultipleVisibleFieldValues<Type>(
    jobs: Type[],
    visibility: FieldVisibility,
    fieldKeys: string[],
): boolean {
    return fieldKeys.some((key) => !visibility.isHidden(key) && hasMultipleValues(jobs, key));
}
